"use client";
import React, { useState, useRef } from "react";
import {
  Search,
  Plus,
  Edit,
  MessageSquare,
  X,
  Bold,
  Italic,
  Underline,
  Image,
  Link as LinkIcon,
} from "lucide-react";

export default function BlogPosts() {
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const editorRef = useRef(null);

  const blogPosts = [
    {
      title: "Why your online store needs a wish list",
      language: "EN",
      comments: 1,
      createdOn: "02/11/2025 10:34:47 PM",
    },
    {
      title: "How a blog can help your growing e-Commerce business",
      language: "EN",
      comments: 1,
      createdOn: "02/11/2025 10:34:46 PM",
    },
  ];

  const filtered = blogPosts.filter((post) =>
    post.title.toLowerCase().includes(search.toLowerCase())
  );

  // Toolbar actions
  const formatText = (command, value = null) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand(command, false, value);
    }
  };

  const handleImageInsert = () => {
    const url = prompt("Enter image URL:");
    if (url) formatText("insertImage", url);
  };

  const handleLinkInsert = () => {
    const url = prompt("Enter link URL:");
    if (url) formatText("createLink", url);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
        <h1 className="text-2xl font-semibold text-gray-800 mb-3 sm:mb-0">
          Blog posts
        </h1>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
        >
          <Plus size={16} />
          Add new
        </button>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
          <div>
            <label className="text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              placeholder="Search title..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mt-1 border border-gray-300 rounded-lg px-3 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm">
            <Search size={16} />
            Search
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-700">
              <th className="px-4 py-3 text-left border-b">Title</th>
              <th className="px-4 py-3 text-left border-b">Language</th>
              <th className="px-4 py-3 text-left border-b">Comments</th>
              <th className="px-4 py-3 text-left border-b">Created on</th>
              <th className="px-4 py-3 text-left border-b">Edit</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? (
              filtered.map((post, i) => (
                <tr
                  key={i}
                  className="hover:bg-gray-50 text-gray-800 border-b"
                >
                  <td className="px-4 py-3">{post.title}</td>
                  <td className="px-4 py-3">{post.language}</td>
                  <td className="px-4 py-3">
                    <button className="flex items-center gap-1 text-blue-600 hover:underline">
                      <MessageSquare size={14} />
                      View comments - {post.comments} (0)
                    </button>
                  </td>
                  <td className="px-4 py-3">{post.createdOn}</td>
                  <td className="px-4 py-3">
                    <button className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-lg text-gray-700">
                      <Edit size={14} />
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="7"
                  className="text-center py-6 text-gray-500 text-sm"
                >
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-start sm:items-center overflow-y-auto py-10 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full sm:w-11/12 md:w-4/5 lg:w-3/5 xl:w-1/2 max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Add a new blog post
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>

            {/* Title */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 mt-1 focus:ring-2 focus:ring-blue-500"
                placeholder="Enter title"
              />
            </div>

            {/* Custom Text Editor */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Body
              </label>

              {/* Toolbar */}
              <div className="flex flex-wrap gap-2 mb-2 border border-gray-300 rounded-lg p-2 bg-gray-50">
                <button
                  onClick={() => formatText("bold")}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <Bold size={16} />
                </button>
                <button
                  onClick={() => formatText("italic")}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <Italic size={16} />
                </button>
                <button
                  onClick={() => formatText("underline")}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <Underline size={16} />
                </button>
                <button
                  onClick={handleLinkInsert}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <LinkIcon size={16} />
                </button>
                <button
                  onClick={handleImageInsert}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <Image size={16} />
                </button>
              </div>

              {/* Editable Area */}
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                className="w-full min-h-[200px] border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 overflow-y-auto"
              ></div>
            </div>

            {/* Body Overview */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Body Overview
              </label>
              <textarea
                rows="3"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 mt-1 focus:ring-2 focus:ring-blue-500"
              ></textarea>
            </div>

            {/* Tags */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Tags
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 mt-1"
                placeholder="Enter tags..."
              />
            </div>

            {/* SEO Section */}
            <h3 className="text-gray-700 font-semibold mt-6 mb-2">SEO</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Meta title
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Meta keywords
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 mt-1"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Meta description
                </label>
                <textarea
                  rows="2"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 mt-1"
                ></textarea>
              </div>
            </div>
            {/* Buttons */}
            <div className="flex justify-end gap-3 mt-6 border-t pt-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
